import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { Lock, Users, Settings, Shield, Key, Database } from "lucide-react";
import { ContentHeader, ContentSection, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/concepts/rbac-abac')({
  component: RBACvABACPage,
  head: () => ({
    meta: [
      { title: 'RBAC vs ABAC | Matricks' },
      { name: 'description', content: 'Compare role-based (RBAC) and attribute-based (ABAC) access control models. Learn when to use each approach for your authorization needs.' },
    ],
  }),
});

const comparisonData = [
  {
    aspect: "Basic Principle",
    rbac: "Access based on assigned roles",
    abac: "Access based on attributes and policies",
  },
  {
    aspect: "Flexibility",
    rbac: "Limited—new roles needed for new scenarios",
    abac: "High—policies can evaluate any attribute",
  },
  {
    aspect: "Complexity",
    rbac: "Simple to understand and implement",
    abac: "More complex but more powerful",
  },
  {
    aspect: "Scalability",
    rbac: "Role explosion in large organizations",
    abac: "Scales well with complex requirements",
  },
  {
    aspect: "Maintenance",
    rbac: "Easy—manage role assignments",
    abac: "Requires policy management expertise",
  },
  {
    aspect: "Fine-Grained Control",
    rbac: "Limited—role-level granularity",
    abac: "Excellent—can control field-level access",
  },
  {
    aspect: "Context Awareness",
    rbac: "No—static role check",
    abac: "Yes—time, location, device, etc.",
  },
];

const faqItems = [
  {
    question: "Can I use RBAC and ABAC together?",
    answer: "Yes, hybrid approaches are common. Many organizations use RBAC as the foundation and layer ABAC on top for fine-grained decisions. For example, RBAC determines basic access while ABAC policies handle edge cases and context-sensitive rules.",
  },
  {
    question: "When should I use RBAC?",
    answer: "RBAC works well when: you have clearly defined organizational roles, access requirements map naturally to job functions, the organization is not too large, and you don't need context-sensitive or highly dynamic access control.",
  },
  {
    question: "When should I use ABAC?",
    answer: "ABAC is better when: you need fine-grained control (e.g., field-level), access depends on context (time, location, device), you have complex compliance requirements, or role explosion is becoming a problem.",
  },
  {
    question: "What is role explosion?",
    answer: "Role explosion occurs when the number of roles grows unmanageably large to accommodate every access scenario. For example, if you have 10 departments, 5 job levels, and 3 regions, you might end up with 150 roles. ABAC avoids this by using policies instead of discrete roles.",
  },
  {
    question: "What is PBAC?",
    answer: "Policy-Based Access Control (PBAC) is often used interchangeably with ABAC. Both use policies to make access decisions. Some distinguish PBAC as focusing more on the policy language and engine, while ABAC emphasizes the attribute-based evaluation model.",
  },
];

function RBACvABACPage() {
  return (
    <div>
      <ContentHeader
        title="RBAC vs ABAC"
        description="Two fundamental approaches to access control. Role-Based Access Control (RBAC) assigns permissions to roles, while Attribute-Based Access Control (ABAC) evaluates policies based on attributes."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Concepts", href: "/concepts" },
          { label: "RBAC vs ABAC" },
        ]}
        badge="Concept"
      />

      {/* RBAC Section */}
      <ContentSection id="rbac" title="Role-Based Access Control (RBAC)">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="prose prose-gray dark:prose-invert max-w-none">
            <p className="text-muted-foreground leading-relaxed">
              RBAC is the most widely used access control model. Users are assigned to roles, and roles
              are granted permissions. Access is determined by checking if the user's role has the
              required permission.
            </p>

            <h3 className="text-lg font-semibold mt-6 mb-3">How RBAC Works</h3>
            <ul className="space-y-2 text-muted-foreground">
              <li>Users are assigned to one or more roles (e.g., "Admin", "Editor", "Viewer")</li>
              <li>Roles are granted specific permissions (e.g., "read:documents", "write:documents")</li>
              <li>Access check: Does the user's role have the required permission?</li>
            </ul>
          </div>

          <div className="rounded-xl border bg-muted/30 p-6">
            <h4 className="font-medium mb-4">RBAC Example</h4>
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-blue-500" />
                <span><strong>User:</strong> Alice (Role: Editor)</span>
              </div>
              <div className="flex items-center gap-3">
                <Key className="h-5 w-5 text-purple-500" />
                <span><strong>Role:</strong> Editor → [read, write, delete]</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-green-500" />
                <span><strong>Check:</strong> Can Alice write? → Yes (Editor has write)</span>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      {/* ABAC Section */}
      <ContentSection id="abac" title="Attribute-Based Access Control (ABAC)" background="muted">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="prose prose-gray dark:prose-invert max-w-none">
            <p className="text-muted-foreground leading-relaxed">
              ABAC evaluates policies based on attributes of the user, resource, action, and environment.
              It offers fine-grained control and can consider context like time, location, or device.
            </p>

            <h3 className="text-lg font-semibold mt-6 mb-3">How ABAC Works</h3>
            <ul className="space-y-2 text-muted-foreground">
              <li>Policies define conditions using attributes</li>
              <li>User attributes (department, clearance), resource attributes (sensitivity, owner)</li>
              <li>Context attributes (time, location, device)</li>
              <li>Policy engine evaluates all conditions to make access decision</li>
            </ul>
          </div>

          <div className="rounded-xl border bg-card p-6">
            <h4 className="font-medium mb-4">ABAC Example</h4>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <Settings className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span><strong>Policy:</strong> Allow if user.department == resource.department AND time is business hours AND user.device is managed</span>
              </div>
              <div className="flex items-start gap-3">
                <Database className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <span><strong>Attributes:</strong> user.department=Sales, resource.department=Sales, time=10:00AM, device=managed</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-green-500" />
                <span><strong>Result:</strong> Access Granted (all conditions met)</span>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      {/* Comparison Table */}
      <ContentSection
        id="comparison"
        title="Side-by-Side Comparison"
        description="How RBAC and ABAC differ across key dimensions."
      >
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b">
                <th className="py-4 px-4 text-left font-semibold">Aspect</th>
                <th className="py-4 px-4 text-left font-semibold">RBAC</th>
                <th className="py-4 px-4 text-left font-semibold">ABAC</th>
              </tr>
            </thead>
            <tbody>
              {comparisonData.map((row, index) => (
                <tr key={index} className="border-b last:border-0">
                  <td className="py-4 px-4 font-medium">{row.aspect}</td>
                  <td className="py-4 px-4 text-muted-foreground">{row.rbac}</td>
                  <td className="py-4 px-4 text-muted-foreground">{row.abac}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ContentSection>

      {/* Choosing */}
      <ContentSection
        id="choosing"
        title="Which Should You Choose?"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Choose RBAC When:
            </h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>• You have clearly defined organizational roles</li>
              <li>• Access requirements are relatively static</li>
              <li>• You need simple, easy-to-understand permissions</li>
              <li>• Compliance audits require clear role documentation</li>
              <li>• Your organization is small to medium-sized</li>
            </ul>
          </div>

          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Choose ABAC When:
            </h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>• You need field-level or record-level access control</li>
              <li>• Access depends on context (time, location, device)</li>
              <li>• You're experiencing role explosion</li>
              <li>• Regulatory requirements demand fine-grained control</li>
              <li>• You need dynamic, policy-driven access decisions</li>
            </ul>
          </div>
        </div>
      </ContentSection>

      {/* Related Concepts */}
      <ContentSection id="related" title="Related Concepts">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/concepts/zero-trust-security"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Zero-Trust Security</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Modern security architecture
            </p>
          </Link>
          <Link
            to="/concepts/api-gateway"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">API Gateway</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Access control enforcement point
            </p>
          </Link>
          <Link
            to="/features/security"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Enterprise Security</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Matricks security features
            </p>
          </Link>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Flexible Access Control with Matricks"
        description="Matricks supports both RBAC and fine-grained ABAC policies. Control access at the API, record, and field level with our policy engine."
        primaryAction={{ label: "Get Started", href: "/login" }}
        secondaryAction={{ label: "View Security Features", href: "/features/security" }}
        background="primary"
      />
    </div>
  );
}
